/**
 * Server-wide incoming webhook administration.
 *
 * Channels come from the admin endpoint instead of the room sidebar so a
 * private channel remains manageable even when the administrator is not a
 * member. Once selected, the existing room-scoped API remains the authority
 * for listing and changing that channel's credentials.
 */

import { useMemo, useState } from 'react'
import type { ChangeEvent, ReactElement } from 'react'

import { useAdminWebhookChannels } from '@/api/queries'
import { RoomWebhooks } from '@/components/RoomWebhooks'
import { Select, Spinner } from '@/components/ui'

import { AdminPage, Card, ErrorNotice } from './AdminLayout'
import styles from './WebhooksPage.module.css'
import admin from './admin.module.css'

export function WebhooksPage(): ReactElement {
  const channelsQuery = useAdminWebhookChannels()
  const [chosenId, setChosenId] = useState<string | null>(null)

  const channels = useMemo(
    () =>
      [...(channelsQuery.data ?? [])].sort((left, right) =>
        (left.name ?? left.slug ?? '').localeCompare(right.name ?? right.slug ?? ''),
      ),
    [channelsQuery.data],
  )

  const selected = channels.find((channel) => channel.id === chosenId) ?? channels[0]
  const options = channels.map((channel) => ({
    value: channel.id,
    label: `#${channel.name ?? channel.slug ?? 'channel'}`,
  }))

  return (
    <AdminPage
      title="Webhooks"
      lead="Manage the external systems and scripts that are allowed to post into channels on this server."
    >
      <Card
        title="Channel"
        lead="Choose where the integration posts. Private channels are listed even when you are not a member."
      >
        {channelsQuery.isPending ? (
          <div className={styles.loading}>
            <Spinner label="Loading channels" />
          </div>
        ) : channelsQuery.error !== null ? (
          <ErrorNotice error={channelsQuery.error} title="The channels could not be loaded" />
        ) : channels.length === 0 ? (
          <p className={admin.muted}>There are no active local channels to connect yet.</p>
        ) : (
          <div className={styles.picker}>
            <Select
              label="Channel"
              value={selected?.id ?? ''}
              options={options}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => setChosenId(event.target.value)}
            />
          </div>
        )}
      </Card>

      {selected === undefined ? null : (
        <Card
          title={`#${selected.name ?? selected.slug ?? 'channel'}`}
          lead="Incoming webhooks for this channel. A webhook URL is a credential, so copy and store it when it is issued."
        >
          <RoomWebhooks room={selected} />
        </Card>
      )}
    </AdminPage>
  )
}

export default WebhooksPage
